<script setup lang="ts">
import { ref, computed, watchEffect } from 'vue';
import axios from 'axios';
import api from '~/api';

const emit = defineEmits<{
  (e: 'result', payload: { order_id: string; task_id?: string; results: { url: string }[] }): void;
  (e: 'state', s: { canGenerate: boolean; label: string; costLabel: string; busy: boolean }): void;
}>();
const { t } = useI18n();

// ─── Provider definitions ───────────────────────────────────
type I2IProvider = 'gemini' | 'seedream' | 'seedream45' | 'nanobanana' | 'aipose-image';

const PROVIDERS: { id: I2IProvider; label: string; badge?: string; maxImages: number }[] = [
  { id: 'aipose-image', label: 'Bestfaceswap 4.0', badge: 'HOT', maxImages: 3 },
  { id: 'seedream45', label: 'Seedream 4.5', badge: 'NEW', maxImages: 10 },
  { id: 'seedream', label: 'Seedream 4.0', maxImages: 10 },
  { id: 'gemini', label: 'Gemini', maxImages: 4 },
  { id: 'nanobanana', label: 'Nano Banana Pro', maxImages: 10 },
];

const ASPECT_OPTIONS = ['1:1', '3:4', '4:3', '9:16', '16:9', '2:3', '3:2'] as const;
const RESOLUTION_OPTIONS = ['1K', '2K', '4K'] as const;
const QUANTITY_OPTIONS = [1, 2, 3, 4] as const;

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

// ─── Preset prompts ─────────────────────────────────────────
const PRESET_PROMPTS = [
  'Transform this image into a cinematic movie poster style',
  'Apply a vibrant watercolor painting effect to this photo',
  'Convert this photo into an anime-style illustration',
  'Make this image look like a vintage oil painting',
  'Give this photo a futuristic cyberpunk aesthetic',
  'Transform this into a professional studio portrait',
  'Apply a dreamy ethereal glow effect to this image',
  'Convert this photo into a detailed pencil sketch',
];

// ─── State ──────────────────────────────────────────────────
const provider = ref<I2IProvider>('aipose-image');
const prompt = ref('');
const aspect = ref<typeof ASPECT_OPTIONS[number]>('1:1');
const resolution = ref<typeof RESOLUTION_OPTIONS[number]>('2K');
const quantity = ref<number>(1);
const quality = ref<'HD' | 'FHD'>('HD');
const styleMode = ref(false);

const submitting = ref(false);
const errorMsg = ref('');

// ─── Image upload state ─────────────────────────────────────
interface UploadedImage {
  id: string;
  url: string;       // R2 URL for API submission
  preview: string;   // local preview (object URL)
  uploading: boolean;
}
const uploadedImages = ref<UploadedImage[]>([]);
const dragOver = ref(false);

const currentMaxImages = computed(() =>
  PROVIDERS.find((p) => p.id === provider.value)?.maxImages ?? 3,
);

// ─── Conditional visibility ─────────────────────────────────
const showResolution = computed(() => ['nanobanana', 'seedream', 'seedream45'].includes(provider.value));
const showHdFhd = computed(() => provider.value === 'aipose-image');
const showStyleMode = computed(() => provider.value === 'aipose-image');

// ─── Cost ───────────────────────────────────────────────────
const costPerImage = computed(() => {
  const p = provider.value;
  const r = resolution.value;
  if (p === 'nanobanana') return r === '4K' ? 30 : r === '2K' ? 20 : 15;
  if (p === 'seedream' || p === 'seedream45') return r === '4K' ? 12 : 6;
  if (p === 'aipose-image') return quality.value === 'FHD' ? 8 : 4;
  return 4;
});
const totalCost = computed(() => costPerImage.value * quantity.value);
const isAnyUploading = computed(() => uploadedImages.value.some((img) => img.uploading));
const readyImages = computed(() => uploadedImages.value.filter((img) => !img.uploading));
const canGenerate = computed(() =>
  !submitting.value && !isAnyUploading.value
  && readyImages.value.length > 0 && prompt.value.trim().length >= 3,
);

function ratioBox(id: string) {
  const [w, h] = id.split(':').map(Number);
  const max = 16;
  return {
    width: `${w >= h ? max : Math.round((max * w) / h)}px`,
    height: `${h >= w ? max : Math.round((max * h) / w)}px`,
  };
}

function randomPrompt() {
  const idx = Math.floor(Math.random() * PRESET_PROMPTS.length);
  prompt.value = PRESET_PROMPTS[idx];
}

// ─── Upload logic ───────────────────────────────────────────
async function uploadFile(file: File) {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    errorMsg.value = 'Only JPG, PNG, WebP images are supported';
    return;
  }
  if (file.size > MAX_FILE_SIZE) {
    errorMsg.value = 'File size must be under 10MB';
    return;
  }
  if (uploadedImages.value.length >= currentMaxImages.value) {
    errorMsg.value = `Maximum ${currentMaxImages.value} images for this model`;
    return;
  }

  const id = `img_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const preview = URL.createObjectURL(file);
  const entry: UploadedImage = { id, url: '', preview, uploading: true };
  uploadedImages.value.push(entry);
  errorMsg.value = '';

  try {
    const ext = (file.name.split('.').pop() || 'png').toLowerCase();
    const sign = await api.getSignUrl(ext);
    await axios.put(sign.sign_url, file, {
      headers: { 'Content-Type': file.type },
    });
    const url = (sign as any).file_url || `https://cdn.treekee.com/bestfaceswap/uploads/${sign.material_id}.${ext}`;
    await api.confirmUpload({
      material_id: sign.material_id,
      url,
      format: ext,
      size: file.size,
    });
    const idx = uploadedImages.value.findIndex((img) => img.id === id);
    if (idx !== -1) {
      uploadedImages.value[idx].url = url;
      uploadedImages.value[idx].uploading = false;
    }
  } catch (e: any) {
    // Remove failed upload
    uploadedImages.value = uploadedImages.value.filter((img) => img.id !== id);
    URL.revokeObjectURL(preview);
    errorMsg.value = e?.message || 'Upload failed';
  }
}

function handleFiles(files: FileList | File[]) {
  const remaining = currentMaxImages.value - uploadedImages.value.length;
  const toUpload = Array.from(files).slice(0, remaining);
  for (const file of toUpload) {
    void uploadFile(file);
  }
}

function onFileInput(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files?.length) handleFiles(input.files);
  input.value = '';
}

function onDrop(event: DragEvent) {
  event.preventDefault();
  dragOver.value = false;
  if (event.dataTransfer?.files?.length) handleFiles(event.dataTransfer.files);
}

function removeImage(id: string) {
  const img = uploadedImages.value.find((i) => i.id === id);
  if (img) URL.revokeObjectURL(img.preview);
  uploadedImages.value = uploadedImages.value.filter((i) => i.id !== id);
}

// ─── Generate ───────────────────────────────────────────────
async function generate() {
  if (!canGenerate.value) return;
  errorMsg.value = '';
  submitting.value = true;

  try {
    const imageUrls = readyImages.value.map((img) => img.url);
    const payload: Record<string, any> = {
      provider: provider.value,
      prompt: prompt.value.trim(),
      image_urls: imageUrls,
      ratio: aspect.value,
      quantity: quantity.value,
    };
    if (showResolution.value) payload.resolution = resolution.value;
    if (showHdFhd.value) payload.quality = quality.value;
    if (showStyleMode.value) payload.styleMode = styleMode.value;

    const r = await api.createImageToImage(payload);
    submitting.value = false;

    // Emit immediately — gallery will show PROCESSING card and poll for async tasks
    emit('result', {
      order_id: r.order_id,
      task_id: r.task_id,
      results: r.results || [],
    });
  } catch (e: any) {
    submitting.value = false;
    errorMsg.value = e?.message || t('imageGen.generationFailed');
  }
}

watchEffect(() => {
  emit('state', {
    canGenerate: canGenerate.value,
    label: submitting.value ? t('imageGen.submitting') : t('imageGen.generate'),
    costLabel: t('imageGen.creditsCount', { n: totalCost.value }),
    busy: submitting.value,
  });
});
defineExpose({ generate });
</script>

<template>
  <div class="panel">
    <div class="head">
      <h2>{{ t('imageGen.imageToImage') }}</h2>
    </div>

    <!-- Upload zone -->
    <div class="section">
      <div class="upload-header">
        <h3>{{ t('editor.uploadPhoto') }}</h3>
        <span class="upload-count">{{ uploadedImages.length }}/{{ currentMaxImages }}</span>
      </div>
      <div
        v-if="uploadedImages.length < currentMaxImages"
        class="drop-zone"
        :class="{ 'drag-over': dragOver }"
        @dragover.prevent="dragOver = true"
        @dragleave="dragOver = false"
        @drop="onDrop"
      >
        <label class="drop-label">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            class="hidden"
            @change="onFileInput"
          />
          <svg class="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <span class="drop-text">{{ t('editor.dropTitle') }}</span>
          <span class="drop-sub">JPG, PNG, WebP &middot; 10MB max</span>
        </label>
      </div>

      <!-- Preview grid -->
      <div v-if="uploadedImages.length > 0" class="preview-grid">
        <div v-for="img in uploadedImages" :key="img.id" class="preview-item">
          <img :src="img.preview" alt="" class="preview-img" />
          <span v-if="img.uploading" class="uploading-overlay">
            <span class="mini-spinner" />
          </span>
          <button v-else type="button" class="remove-btn" @click="removeImage(img.id)">&times;</button>
        </div>
      </div>
    </div>

    <!-- Prompt -->
    <div class="section">
      <div class="prompt-head">
        <h3>Prompt</h3>
        <button type="button" class="random-btn" @click="randomPrompt">{{ t('imageGen.presetPrompt') }}</button>
      </div>
      <textarea v-model="prompt" rows="3" :placeholder="t('imageGen.promptPlaceholder')" />
    </div>

    <!-- Model -->
    <div class="section">
      <h3>{{ t('imageGen.model') }}</h3>
      <select v-model="provider" class="model-select">
        <option v-for="p in PROVIDERS" :key="p.id" :value="p.id">{{ p.label }}{{ p.badge ? ` [${p.badge}]` : '' }}</option>
      </select>
    </div>

    <!-- Ratio -->
    <div class="section">
      <h3>{{ t('imageGen.ratio') }}</h3>
      <div class="ratio-grid">
        <button
          v-for="a in ASPECT_OPTIONS" :key="a"
          class="ratio-btn"
          :class="{ active: aspect === a }"
          @click="aspect = a"
        >
          <span class="ratio-box" :style="ratioBox(a)" />
          <span>{{ a }}</span>
        </button>
      </div>
    </div>

    <!-- Quantity -->
    <div class="section">
      <h3>{{ t('imageGen.quantity') }}</h3>
      <div class="qty-grid">
        <button
          v-for="q in QUANTITY_OPTIONS" :key="q"
          class="qty-btn"
          :class="{ active: quantity === q }"
          @click="quantity = q"
        >{{ q }}</button>
      </div>
    </div>

    <!-- Resolution -->
    <div v-if="showResolution" class="section">
      <h3>{{ t('imageGen.resolution') }}</h3>
      <div class="qty-grid">
        <button
          v-for="r in RESOLUTION_OPTIONS" :key="r"
          class="qty-btn"
          :class="{ active: resolution === r }"
          @click="resolution = r"
        >{{ r }}</button>
      </div>
    </div>

    <!-- AiPose quality -->
    <div v-if="showHdFhd" class="section">
      <h3>{{ t('imageGen.resolution') }}</h3>
      <div class="qty-grid">
        <button class="qty-btn" :class="{ active: quality === 'HD' }" @click="quality = 'HD'">HD</button>
        <button class="qty-btn" :class="{ active: quality === 'FHD' }" @click="quality = 'FHD'">FHD</button>
      </div>
    </div>

    <!-- AiPose styleMode -->
    <div v-if="showStyleMode" class="toggles">
      <label class="toggle">
        <span class="toggle-label">Photoreal</span>
        <input type="checkbox" v-model="styleMode" />
        <span class="switch" />
      </label>
    </div>

    <!-- Generate -->
    <div class="section">
      <button class="cta" :disabled="!canGenerate" @click="generate">
        <span class="cta-text">
          {{ submitting ? t('imageGen.submitting') : t('imageGen.generate') }}
        </span>
        <span class="cost">{{ t('imageGen.creditsCount', { n: totalCost }) }}</span>
      </button>
    </div>

    <p v-if="errorMsg" class="err">{{ errorMsg }}</p>
  </div>
</template>

<style scoped lang="scss">
.panel {
  @apply rounded-2xl border border-neutral-800 bg-neutral-900/60 p-5 space-y-4 text-white;
}
.head h2 { @apply text-lg font-bold; }
.section { @apply pt-2 space-y-2; }
.section h3 { @apply text-sm font-semibold; }
.prompt-head { @apply flex items-center justify-between; }
.random-btn { @apply text-xs text-amber-400 hover:text-amber-300 transition; }
.section textarea {
  @apply w-full rounded-md border border-neutral-700 bg-neutral-950/60 p-2.5 text-sm text-white placeholder-white/40
    focus:outline-none focus:border-amber-500 resize-y;
}
.upload-header {
  @apply flex items-center justify-between;
}
.upload-count {
  @apply text-xs text-white/50 font-mono;
}
.drop-zone {
  @apply rounded-xl border-2 border-dashed border-neutral-700 bg-neutral-950/40 transition-colors;
  &.drag-over { @apply border-amber-500 bg-amber-500/5; }
}
.drop-label {
  @apply flex flex-col items-center justify-center gap-1.5 py-6 cursor-pointer;
}
.upload-icon {
  @apply w-8 h-8 text-white/40;
}
.drop-text {
  @apply text-sm text-white/70;
}
.drop-sub {
  @apply text-xs text-white/30;
}
.preview-grid {
  @apply flex gap-2 flex-wrap mt-2;
}
.preview-item {
  @apply relative w-16 h-16 rounded-lg overflow-hidden border border-neutral-700;
}
.preview-img {
  @apply w-full h-full object-cover;
}
.uploading-overlay {
  @apply absolute inset-0 flex items-center justify-center bg-black/50;
}
.mini-spinner {
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid rgba(249, 115, 22, 0.3);
  border-top-color: #f97316;
  border-radius: 9999px;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.remove-btn {
  @apply absolute -right-0.5 -top-0.5 w-5 h-5 rounded-full bg-black/70 text-white text-xs flex items-center justify-center
    hover:bg-red-600 transition-colors cursor-pointer;
}
.model-select {
  @apply w-full rounded-lg bg-neutral-950/60 border border-neutral-700 py-2.5 px-3 text-sm text-white
    focus:outline-none focus:border-amber-500 appearance-none cursor-pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='white' viewBox='0 0 16 16'%3E%3Cpath d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  padding-right: 2rem;
  option { @apply bg-neutral-900 text-white; }
}
.ratio-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.5rem;
}
.ratio-btn {
  @apply flex flex-row items-center justify-center gap-1.5 rounded-full bg-neutral-950/60 border border-neutral-800 py-2 text-xs transition;
  &.active { @apply bg-white text-neutral-900 border-white; }
}
.ratio-box {
  display: inline-block; flex: none;
  border: 1.5px solid currentColor; border-radius: 2px; opacity: 0.8;
}
.ratio-btn.active .ratio-box { opacity: 1; }
.qty-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.5rem;
}
.qty-btn {
  @apply rounded-full bg-neutral-950/60 border border-neutral-800 py-2 text-xs font-medium transition text-center;
  &.active { @apply bg-white text-neutral-900 border-white; }
}
.toggles { @apply space-y-2 pt-2; }
.toggle {
  @apply flex items-center justify-between rounded-xl bg-neutral-950/60 border border-neutral-800 px-4 py-3 cursor-pointer;
  input { @apply hidden; }
}
.toggle-label { @apply text-sm font-medium; }
.switch {
  @apply w-10 h-6 rounded-full bg-neutral-700 relative transition;
  &::after {
    content: '';
    @apply absolute size-5 rounded-full bg-white top-0.5 left-0.5 transition-all;
  }
}
.toggle input:checked + .switch { @apply bg-amber-500; }
.toggle input:checked + .switch::after { @apply left-[1.125rem]; }
.cta {
  @apply flex items-center justify-between gap-2 w-full rounded-full px-6 py-3 text-base font-semibold text-white transition-all
    disabled:opacity-40 disabled:cursor-not-allowed;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 50%, #c2410c 100%);
  box-shadow: 0 8px 24px -8px rgba(249,115,22,0.5);
}
.cta-text { @apply flex-1 text-left; }
.cost { @apply rounded-full bg-black/30 px-3 py-0.5 text-xs; }
.err { @apply text-sm text-red-400; }
</style>
